/**
 * `<ResponsiveModal/>` — adaptive modal presentation (#966 P1.5; panels
 * since #1922).
 *
 * The cross-cutting "one surface, the right presentation per form factor"
 * primitive. Mobile (<768px, via `useIsMobile`) renders `ui/BottomSheet`.
 * Desktop renders one of two things, picked by `presentation`:
 *
 *   - `"modal"` (the default here, so callers that never asked keep what
 *     they had) — the centred `ui/Dialog` with its scrim. For confirms
 *     whose whole content is in the box.
 *   - `"panel"` (#1922, R6 of
 *     `docs/agent-prompts/handover-right-panel-and-inbox-2026-10-02.md`) —
 *     the non-modal `ui/SideDrawer` at the page's right edge: no scrim, no
 *     focus trap, the page behind stays readable, scrollable and clickable,
 *     because the page is what the user decides against. It holds the one
 *     right-edge drawer slot (`lib/drawerSlot`): opening it closes the
 *     lead's next-steps drawer and any other open panel.
 *
 * Both desktop shapes keep the same contract: `testid` on the surface, the
 * body `${testid}-body`, the title as the dialog's accessible name and the
 * description wired as `aria-describedby`, so a surface's tests keep their
 * anchors whichever it is. `cycle/CycleSurfaceModal` (every roster
 * `?surface=`) defaults to `"panel"`.
 */
import type { ReactNode, RefObject } from "react";

import { cn } from "../../lib/cn";
import { useDrawerSlot } from "../../lib/drawerSlot";
import { useIsMobile } from "../../lib/useMediaQuery";
import { BottomSheet, type BottomSheetSnap } from "./BottomSheet";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./Dialog";
import { SideDrawer, type SideDrawerWidth } from "./SideDrawer";

export type ResponsiveModalPresentation = "panel" | "modal";

export interface ResponsiveModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** User-visible title. Keep state-mechanic (brand-pillar copy). */
  title: ReactNode;
  /** Optional one-line subtitle below the title. */
  description?: ReactNode | undefined;
  children: ReactNode;
  /** Per-surface testid; the body gets `${testid}-body` (matches the prior chrome). */
  testid?: string | undefined;
  /** Desktop `"modal"` only: a `DialogContent` width override (e.g.
   *  `max-w-5xl`). The panel takes `panelWidth`; the phone sheet is
   *  full-width. */
  desktopContentClassName?: string | undefined;
  /** Desktop presentation: `"modal"` (default) or `"panel"` (#1922). */
  presentation?: ResponsiveModalPresentation | undefined;
  /** Desktop `"panel"` width: `md` 360 · `lg` 480 (default) · `xl` 720 px. */
  panelWidth?: SideDrawerWidth | undefined;
  /** Phone sheet height. Omitted = today's full sheet with its scrim.
   *  `"half"` opens at half the screen with no scrim, the page above
   *  usable; a chevron toggles full height (#1922). */
  snap?: BottomSheetSnap | undefined;
  /** Desktop `"panel"`: where focus returns on close, when the opener is
   *  not the element that held focus at open (a menu item that unmounts). */
  returnFocusRef?: RefObject<HTMLElement | null> | undefined;
  /** Desktop `"panel"`: where focus goes on close when the opener is gone
   *  (a deep-link open) — the control that opens this surface (#1934). */
  fallbackFocusRef?: RefObject<HTMLElement | null> | undefined;
}

export function ResponsiveModal({
  open,
  onOpenChange,
  title,
  description,
  children,
  testid,
  desktopContentClassName,
  presentation = "modal",
  panelWidth = "lg",
  snap,
  returnFocusRef,
  fallbackFocusRef,
}: ResponsiveModalProps) {
  const isMobile = useIsMobile();
  const asPanel = !isMobile && presentation === "panel";
  // One right-edge drawer at a time: a panel claims the slot while open
  // and closes when another panel (or the next-steps drawer) takes it.
  useDrawerSlot(open && asPanel, () => onOpenChange(false));

  if (isMobile) {
    return (
      <BottomSheet open={open} onOpenChange={onOpenChange} title={title} snap={snap}>
        <div className="px-5 pb-4" data-testid={testid}>
          {description ? <p className="mb-3 text-sm text-fg-subtle">{description}</p> : null}
          <div className="flex flex-col gap-4" data-testid={testid ? `${testid}-body` : undefined}>
            {children}
          </div>
        </div>
      </BottomSheet>
    );
  }

  if (asPanel) {
    return (
      <SideDrawer
        open={open}
        onOpenChange={onOpenChange}
        title={title}
        description={description}
        width={panelWidth}
        testid={testid}
        returnFocusRef={returnFocusRef}
        fallbackFocusRef={fallbackFocusRef}
      >
        {/* The drawer's body carries no padding; the surface's content
            gets the same column + gap it had inside the dialog. */}
        <div className="flex flex-col gap-4 p-4">{children}</div>
      </SideDrawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn("max-w-lg", desktopContentClassName)} data-testid={testid}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        <div className="flex flex-col gap-4" data-testid={testid ? `${testid}-body` : undefined}>
          {children}
        </div>
      </DialogContent>
    </Dialog>
  );
}
